import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { useState } from "react";

const isSponsorPhotoUrl = (photoUrl) => {
  if (!photoUrl || typeof photoUrl !== "string") {
    return false;
  }

  return photoUrl.includes("/storage/sponsors/");
};

export default function SponsorList({
  currentPage,
  currentSponsors,
  expandedSponsorId,
  filteredSponsors,
  itemsPerPage,
  onDelete,
  onEdit,
  onPageChange,
  onSearchChange,
  onToggleExpand,
  totalPages,
}) {
  const [failedPhotos, setFailedPhotos] = useState({});

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "16px",
        border: "2px solid #e0f2e9",
        backgroundColor: "#fcffff",
      }}
    >
      <TextField
        label="Buscar patrocinador"
        fullWidth
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": { borderRadius: "12px" },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#FFA500" }} />
            </InputAdornment>
          ),
        }}
        onChange={onSearchChange}
      />

      <Typography
        variant="h6"
        sx={{
          mb: 1,
          color: "#0D0D0D",
          fontFamily: "'Comic Relief'",
          fontWeight: "bold",
        }}
      >
        Lista de Patrocinadores
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <List>
        {currentSponsors.map((sponsor) => {
          const isExpanded = expandedSponsorId === sponsor.id;
          const hasAccessiblePhoto =
            isSponsorPhotoUrl(sponsor.photo_url) && !failedPhotos[sponsor.id];

          return (
            <ListItem
              key={sponsor.id}
              alignItems="flex-start"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                width: "100%",
                borderBottom: "1px solid #e0f2e9",
                cursor: "pointer",
                px: 2,
                py: 1,
                "&:hover": {
                  backgroundColor: "rgba(27, 141, 75, 0.05)",
                },
              }}
              onClick={() => onToggleExpand(sponsor.id)}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                  {hasAccessiblePhoto ? (
                    <Box
                      component="img"
                      src={sponsor.photo_url}
                      alt={sponsor.business_name}
                      onError={() =>
                        setFailedPhotos((prev) => ({ ...prev, [sponsor.id]: true }))
                      }
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        objectFit: "cover",
                        flexShrink: 0,
                        border: "2px solid #e0f2e9",
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        flexShrink: 0,
                        bgcolor: "#e0f2e9",
                        color: "#1B8D4B",
                        fontWeight: 700,
                      }}
                    >
                      {sponsor.business_name?.charAt(0)?.toUpperCase() || "P"}
                    </Avatar>
                  )}
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontFamily: "'Comic Relief', cursive",
                          fontWeight: 600,
                          color: "#1B8D4B",
                        }}
                      >
                        {sponsor.business_name}
                      </Typography>
                    }
                    secondary={`Tel: ${sponsor.phone}`}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(sponsor);
                    }}
                    sx={{
                      color: "#1B8D4B",
                      "&:hover": {
                        backgroundColor: "rgba(27, 141, 75, 0.1)",
                      },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(sponsor.id);
                    }}
                    sx={{
                      color: "#ff4444",
                      "&:hover": {
                        backgroundColor: "rgba(255, 0, 0, 0.1)",
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              {isExpanded && (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {hasAccessiblePhoto && (
                    <Box
                      component="img"
                      src={sponsor.photo_url}
                      alt={sponsor.business_name}
                      onError={() =>
                        setFailedPhotos((prev) => ({ ...prev, [sponsor.id]: true }))
                      }
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        objectFit: "cover",
                        border: "2px solid #e0f2e9",
                      }}
                    />
                  )}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "'Comic Relief', cursive",
                        color: "#555",
                      }}
                    >
                      <Box component="span" fontWeight="600">
                        Direccion:
                      </Box>{" "}
                      {sponsor.address}
                      <br />
                      <Box component="span" fontWeight="600">
                        Notas:
                      </Box>{" "}
                      {sponsor.notes || "Sin notas"}
                      <br />
                      {sponsor.links?.length > 0 && (
                        <>
                          <Box component="span" fontWeight="600">
                            Enlaces:
                          </Box>{" "}
                          {sponsor.links.map((link, i) => (
                            <Box key={link.id ?? `${link.title}-${i}`}>
                              - {link.title}: {link.url}
                            </Box>
                          ))}
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}
            </ListItem>
          );
        })}
      </List>

      {filteredSponsors.length > itemsPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={onPageChange}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#1B8D4B",
                "&.Mui-selected": {
                  backgroundColor: "#1B8D4B",
                  color: "white",
                },
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
}
